import { Button, Sheet, SheetBody, SheetFooter, SheetHeader, TextField } from "@community-project-org/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import type { components } from "../../generated/api.js";
import { fromZonedInput, toZonedInput } from "./zonedInput.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type StopDTO = components["schemas"]["StopDTO"];

/** Ein Halt im Formular: Zeiten als Ortszeit des Halts, Preis als Eurotext. */
interface StopDraft {
  name: string;
  timeZone: string;
  arrivesAt: string;
  departsAt: string;
  fare: string;
}

const DEFAULT_ZONE = "Europe/Berlin";

function toDraft(stop: StopDTO): StopDraft {
  return {
    name: stop.name,
    timeZone: stop.timeZone,
    arrivesAt: toZonedInput(stop.arrivesAt, stop.timeZone),
    departsAt: toZonedInput(stop.departsAt, stop.timeZone),
    fare: (stop.fareCents / 100).toFixed(2),
  };
}

function emptyStop(zone: string): StopDraft {
  return { name: "", timeZone: zone, arrivesAt: "", departsAt: "", fare: "0.00" };
}

const inputClass = "min-h-[44px] rounded-md border border-line bg-surface px-2 text-[14px] text-ink";

/**
 * Halte einer Fahrt pflegen: Name, Ankunft, Abfahrt, Fahrpreis ab Start. Der erste
 * Halt hat nur eine Abfahrt, der letzte nur eine Ankunft. Bleiben alle Fahrpreise 0,
 * kostet jede Teilstrecke den Grundpreis — für einen Shuttle reicht das.
 */
export function StopsSheet({ service, onClose }: { service: ServiceDTO; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const path = `/services/${encodeURIComponent(service.id)}/stops`;
  const current = useQuery({ queryKey: ["service-stops", service.id], queryFn: () => apiRequest<StopDTO[]>(path) });
  const [drafts, setDrafts] = useState<StopDraft[] | null>(null);
  const stops = drafts ?? (current.data && current.data.length > 0 ? current.data.map(toDraft) : [emptyStop(DEFAULT_ZONE), emptyStop(DEFAULT_ZONE)]);

  const save = useMutation({
    mutationFn: (list: StopDraft[]) =>
      apiRequest<StopDTO[]>(path, {
        method: "PUT",
        body: {
          stops: list.map((stop) => ({
            name: stop.name,
            timeZone: stop.timeZone,
            arrivesAt: fromZonedInput(stop.arrivesAt, stop.timeZone),
            departsAt: fromZonedInput(stop.departsAt, stop.timeZone),
            fareCents: Math.round(Number(stop.fare.replace(",", ".")) * 100) || 0,
          })),
        },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["services"] }),
        queryClient.invalidateQueries({ queryKey: ["service-stops", service.id] }),
      ]);
      onClose();
    },
  });

  const update = (index: number, patch: Partial<StopDraft>): void =>
    setDrafts(stops.map((stop, i) => (i === index ? { ...stop, ...patch } : stop)));

  return (
    <Sheet open onClose={onClose} label={`Halte: ${service.title}`} size="lg">
      <SheetHeader>
        <h2 className="text-[18px] font-extrabold text-ink">Halte · {service.title}</h2>
      </SheetHeader>
      <SheetBody>
        {current.isPending ? (
          <p className="text-[14px] text-ink-3">Wird geladen …</p>
        ) : (
          <form
            id="stops-form"
            className="grid gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate(stops);
            }}
          >
            {stops.map((stop, index) => {
              const first = index === 0;
              const last = index === stops.length - 1;
              return (
                <fieldset key={index} className="grid gap-2 rounded border border-line p-3">
                  <legend className="micro px-1">{first ? "Start" : last ? "Ziel" : `Halt ${index}`}</legend>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <TextField label="Name" required value={stop.name} onChange={(event) => update(index, { name: event.target.value })} />
                    </div>
                    {stops.length > 2 && (
                      <Button variant="ghost" aria-label="Halt entfernen" onClick={() => setDrafts(stops.filter((_, i) => i !== index))}>
                        <Trash2 size={16} aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <label className="grid gap-1 text-[12px] font-bold text-ink-2">
                      Ankunft
                      <input
                        type="datetime-local"
                        className={inputClass}
                        disabled={first}
                        required={!first}
                        value={stop.arrivesAt}
                        onChange={(event) => update(index, { arrivesAt: event.target.value })}
                      />
                    </label>
                    <label className="grid gap-1 text-[12px] font-bold text-ink-2">
                      Abfahrt
                      <input
                        type="datetime-local"
                        className={inputClass}
                        disabled={last}
                        required={!last}
                        value={stop.departsAt}
                        onChange={(event) => update(index, { departsAt: event.target.value })}
                      />
                    </label>
                    <TextField
                      label="Preis ab Start (€)"
                      inputMode="decimal"
                      disabled={first}
                      value={stop.fare}
                      onChange={(event) => update(index, { fare: event.target.value })}
                    />
                    <TextField label="Zeitzone" value={stop.timeZone} onChange={(event) => update(index, { timeZone: event.target.value })} />
                  </div>
                </fieldset>
              );
            })}
            <Button
              variant="secondary"
              onClick={() => {
                const next = [...stops];
                next.splice(stops.length - 1, 0, emptyStop(stops[stops.length - 2]?.timeZone ?? DEFAULT_ZONE));
                setDrafts(next);
              }}
            >
              <Plus size={16} aria-hidden="true" />
              Halt vor dem Ziel einfügen
            </Button>
            {save.error && (
              <p className="text-[13px] text-danger" role="alert">
                {save.error instanceof ApiRequestError ? save.error.message : "Das hat nicht geklappt."}
              </p>
            )}
          </form>
        )}
      </SheetBody>
      <SheetFooter>
        {(current.data?.length ?? 0) > 0 && (
          <Button variant="ghost" disabled={save.isPending} onClick={() => save.mutate([])}>
            Strecke entfernen
          </Button>
        )}
        <span className="flex-1" />
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
        <Button type="submit" form="stops-form" disabled={save.isPending || current.isPending} aria-busy={save.isPending}>
          {save.isPending ? "Wird gespeichert …" : "Speichern"}
        </Button>
      </SheetFooter>
    </Sheet>
  );
}
